import { useState } from 'react';
import {
	IoIosCheckmarkCircle,
	IoIosCloseCircle,
	IoIosHelpCircle,
} from 'react-icons/io';

import Loader from '../Loader';
import Pagination from '../Pagination';
import { eventStatusColor } from '../utils/eventStatusColor';
import { tableBackgroundColor } from '../utils/tableBackgroundColor';

import useChangeEventStatus from '@/hooks/events/changeEventStatus';
import { useGetAllEvents } from '@/hooks/events/useGetAllEvents';
import { EventStatus, EventType } from '@/interfaces/entities/event';

const EventsTable = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const { isLoading, isError, events, totalPages, isPending } =
		useGetAllEvents(currentPage);
	const { mutate } = useChangeEventStatus();
	const isFetching = isLoading || isPending;

	if (isFetching) {
		return <Loader />;
	}

	if (isError) {
		return <div>Error...</div>;
	}

	const eventStatus = (status: EventStatus) => {
		const color = eventStatusColor(status);
		switch (status) {
			case EventStatus.APPROVED:
				return (
					<IoIosCheckmarkCircle
						className={`${color} inline-block align-middle`}
						title={EventStatus.APPROVED}
						size={22}
					/>
				);
			case EventStatus.REJECTED:
				return (
					<IoIosCloseCircle
						className={`${color} inline-block align-middle`}
						title={EventStatus.REJECTED}
						size={22}
					/>
				);
			case EventStatus.PENDING:
				return (
					<IoIosHelpCircle
						className={`${color} inline-block align-middle`}
						title={EventStatus.PENDING}
						size={22}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className="px-4 sm:px-6 lg:px-8 w-full mb-10">
			<div className="sm:flex sm:items-center">
				<div className="sm:flex-auto">
					<h1
						data-test="h1-events"
						className="text-base font-semibold leading-6 text-gray-900"
					>
						Events
					</h1>
				</div>
			</div>

				<div className="mt-8 w-full  flow-root">
					<div className=" w-full overflow-x-auto">
						<div className="inline-block  min-w-[1200px] w-full py-2 align-middle">
						<table className="min-w-full table-auto divide-y divide-gray-300 border-red-300 table-auto">
							<thead>
								<tr>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900 "
										data-test="th-id"
									>
										ID
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-title"
									>
										Title
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-description"
									>
										Description
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-visibility"
									>
										Visibility
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-type"
									>
										Type
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-date"
									>
										Date
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-location"
									>
										Location
									</th>
									<th
										scope="col"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
										data-test="th-status"
									>
										Status
									</th>
									<th
										scope="col"
										data-test="th-action"
										className=" px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
									>
										<span className="">Action</span>
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{events.map(
									(
										{
											id,
											title,
											description,
											status,
											date,
											location,
											type,
											visibility,
										},
										index,
									) =>
										id ?(
											<tr
												key={id}
												className={`${tableBackgroundColor(
													index,
												)} hover:bg-gray-200 min-h-8 border-t border-gray-200`}
											>
												<td
													data-test="td-id"
													className="px-3 py-3.5 text-center  text-sm text-gray-900 break-words"
												>
													{id}
												</td>
												<td
													data-test="td-title"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{title}
												</td>
												<td
													data-test="td-description"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{description}
												</td>
												<td
													data-test="td-visibility"
													className="px-3 py-3.5 text-center text-sm text-gray-900 capitalize break-words"
												>
													{visibility}
												</td>
												<td
													data-test="td-type"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{type === EventType.InPerson
														? 'In Person'
														: 'Virtual'}
												</td>
												<td
													data-test="td-date"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{new Date(date).toLocaleDateString()}
												</td>
												<td
													data-test="td-location"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{location}
												</td>
												<td
													data-test="td-status"
													className="px-3 py-3.5 text-center text-sm text-gray-900 break-words"
												>
													{eventStatus(status)}
												</td>
												<td className="px-3 py-3.5 text-center text-sm text-gray-900">
													{status !== EventStatus.APPROVED && (
														<button
															onClick={() =>
																mutate({
																	eventId: id,
																	status: EventStatus.APPROVED,
																})
															}
															className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
															data-test="btn-approve"
															disabled={isFetching}
														>
															Approve
														</button>
													)}
													{status !== EventStatus.REJECTED && (
														<button
															onClick={() =>
																mutate({
																	eventId: id,
																	status: EventStatus.REJECTED,
																})
															}
															className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400"
															disabled={isFetching}
															data-test="btn-reject"
														>
															Reject
														</button>
													)}
												</td>
											</tr>
										):<></>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<Pagination
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				totalPages={totalPages as number}
			/>
		</div>
	);
};

export default EventsTable;
